import { useEffect, useMemo, useState } from 'react'
import type { ComponentProps } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../components/PageHeader'
import {
  Button,
  Card,
  Chip,
  ConfirmDeleteModal,
  EmptyState,
  Input,
  Modal,
  SearchableSelect,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import {
  useAccessGroupEffectiveDoors,
  useAccessGroupMembers,
  useAccessGroups,
  useDirectoryGrants,
  useDoorPolicies,
  useDoors,
  useGroups,
  useLocationTree,
  useMemberRules,
  useMergeAccessGroup,
  usePersons,
  useSchedules,
} from '../api/hooks'
import { api } from '../api/client'
import { useAuthStore } from '../stores/auth'
import type { AccessGroupMember, DirectoryGrant, Door, DoorPolicy, LocationNode, MemberRule } from '../api/types'
import { locationBarriers, locationPaths } from '../lib/locations'
import { readOnlyReason } from '../lib/dryrun'
import { SelectField } from './Directory'
import { LocationLink } from '../components/LocationLink'
import { MergeModal } from '../components/features/MergeModal'

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** Chip tone per membership source — direct assignments stand out in gold. */
function sourceTone(source: AccessGroupMember['source']): ComponentProps<typeof Chip>['tone'] {
  return source === 'direct' ? 'gold' : source === 'group' ? 'info' : 'muted'
}

/** Pre-order flatten (uid + type) for location dropdowns. */
function flatten(nodes: LocationNode[]): { uid: string; type: string }[] {
  return nodes.flatMap((n) => [{ uid: n.unique_id, type: n.type }, ...flatten(n.children)])
}

/** "HQ / Floor 1 / Main Door" — the door's name on its location breadcrumb. */
function doorLabel(door: Door): string {
  return [...door.breadcrumb, door.name].join(' / ')
}

function AddPolicyModal({
  open,
  groupUid,
  takenDoors,
  onClose,
}: {
  open: boolean
  groupUid: string
  /** Doors this group already has a policy for (the API rejects duplicates). */
  takenDoors: Set<string>
  onClose: () => void
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const doors = useDoors()
  const schedules = useSchedules()

  const [door, setDoor] = useState('')
  const [mode, setMode] = useState('include')
  const [schedule, setSchedule] = useState('')

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [scope, 'door-policies'] })
    queryClient.invalidateQueries({ queryKey: [scope, 'access-groups'] })
  }
  const create = useMutation({
    mutationFn: () =>
      api.createDoorPolicy({
        group: groupUid,
        door,
        mode: mode as 'include' | 'exclude',
        schedule: schedule || null,
      }),
    onSuccess: () => {
      invalidate()
      onClose()
    },
  })

  useEffect(() => {
    if (open) {
      setDoor('')
      setMode('include')
      setSchedule('')
      create.reset()
    }
    // create.reset is stable; only reset when the modal (re)opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const doorOptions = useMemo(
    () =>
      (doors.data ?? [])
        .filter((d) => !takenDoors.has(d.unique_id))
        .map((d) => ({ value: d.unique_id, label: doorLabel(d) })),
    [doors.data, takenDoors],
  )

  return (
    <Modal open={open} title="Add door policy" onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (door) create.mutate()
        }}
      >
        {doorOptions.length === 0 ? (
          <p className="text-xs text-ostrel-ink-muted">
            Every door already has a policy for this group.
          </p>
        ) : (
          <SelectField
            label="Door"
            value={door}
            onChange={setDoor}
            options={[{ value: '', label: '— select door —' }, ...doorOptions]}
          />
        )}
        <SelectField
          label="Mode"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'include', label: 'Include — grant access' },
            { value: 'exclude', label: 'Exclude — deny access' },
          ]}
        />
        <SelectField
          label="Schedule"
          value={schedule}
          onChange={setSchedule}
          options={[
            { value: '', label: 'Always' },
            // Door policies only accept door schedules (the API 422s otherwise).
            ...(schedules.data ?? [])
              .filter((s) => s.kind === 'door')
              .map((s) => ({ value: s.unique_id, label: s.name })),
          ]}
        />
        {create.isError && <p className="text-xs text-ostrel-danger">{errText(create.error)}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="navy" type="submit" disabled={create.isPending || !door}>
            {create.isPending ? 'Adding…' : 'Add policy'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

/** Edit a policy's mode + schedule via PUT /door-policies/{uid} (door and group are immutable). */
function EditPolicyModal({
  policy,
  onClose,
}: {
  policy: DoorPolicy | null
  onClose: () => void
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const schedules = useSchedules()

  const [mode, setMode] = useState('include')
  const [schedule, setSchedule] = useState('')

  const update = useMutation({
    mutationFn: () =>
      api.updateDoorPolicy((policy as DoorPolicy).unique_id, {
        mode: mode as 'include' | 'exclude',
        schedule: schedule || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope, 'door-policies'] })
      onClose()
    },
  })

  useEffect(() => {
    if (policy) {
      setMode(policy.mode)
      setSchedule(policy.schedule ?? '')
      update.reset()
    }
    // update.reset is stable; only reset when the modal (re)opens on a policy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy])

  // A schedule only makes sense on an include rule (the backend rejects it otherwise).
  useEffect(() => {
    if (mode === 'exclude') setSchedule('')
  }, [mode])

  return (
    <Modal open={policy !== null} title="Edit door policy" onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          update.mutate()
        }}
      >
        <SelectField
          label="Mode"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'include', label: 'Include — grant access' },
            { value: 'exclude', label: 'Exclude — deny access' },
          ]}
        />
        {mode === 'include' ? (
          <SelectField
            label="Schedule"
            value={schedule}
            onChange={setSchedule}
            options={[
              { value: '', label: 'Always' },
              // Door policies only accept door schedules (the API 422s otherwise).
              ...(schedules.data ?? [])
                .filter((s) => s.kind === 'door')
                .map((s) => ({ value: s.unique_id, label: s.name })),
              // A vendor-derived policy may already reference a non-door schedule
              // the picker excludes — surface it marked instead of showing 'Always'.
              ...(schedule &&
              !(schedules.data ?? []).some((s) => s.unique_id === schedule && s.kind === 'door')
                ? [
                    {
                      value: schedule,
                      label: `${
                        (schedules.data ?? []).find((s) => s.unique_id === schedule)?.name ?? schedule
                      } (vendor-read-only)`,
                    },
                  ]
                : []),
            ]}
          />
        ) : (
          <p className="text-[11px] text-ostrel-ink-muted">
            Exclude rules deny access around the clock — no schedule applies.
          </p>
        )}
        {update.isError && <p className="text-xs text-ostrel-danger">{errText(update.error)}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="navy" type="submit" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function AddRuleModal({
  open,
  groupUid,
  takenLocations,
  onClose,
}: {
  open: boolean
  groupUid: string
  /** Locations this group already has a location rule for (the API rejects duplicates). */
  takenLocations: Set<string>
  onClose: () => void
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const tree = useLocationTree()

  const [location, setLocation] = useState('')

  const create = useMutation({
    mutationFn: () => api.createMemberRule({ group: groupUid, location }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope, 'member-rules'] })
      queryClient.invalidateQueries({ queryKey: [scope, 'access-groups'] })
      onClose()
    },
  })

  useEffect(() => {
    if (open) {
      setLocation('')
      create.reset()
    }
    // create.reset is stable; only reset when the modal (re)opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const paths = useMemo(() => (tree.data ? locationPaths(tree.data) : new Map<string, string>()), [tree.data])
  const barriers = useMemo(() => locationBarriers(tree.data ?? []), [tree.data])
  const locationOptions = useMemo(
    () =>
      flatten(tree.data ?? [])
        .filter((l) => !takenLocations.has(l.uid))
        .map((l) => ({
          value: l.uid,
          label: `${paths.get(l.uid) ?? l.uid}${barriers.has(l.uid) ? ' 🔒' : ''}`,
        })),
    [tree.data, takenLocations, paths, barriers],
  )

  return (
    <Modal open={open} title="Add location rule" onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (location) create.mutate()
        }}
      >
        <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
          Everyone with an active occupancy at or under the location becomes a member of this
          group.
        </p>
        {locationOptions.length === 0 ? (
          <p className="text-xs text-ostrel-ink-muted">
            Every location already has a location rule for this group.
          </p>
        ) : (
          <SelectField
            label="Location"
            value={location}
            onChange={setLocation}
            options={[{ value: '', label: '— select location —' }, ...locationOptions]}
          />
        )}
        {create.isError && <p className="text-xs text-ostrel-danger">{errText(create.error)}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="navy" type="submit" disabled={create.isPending || !location}>
            {create.isPending ? 'Adding…' : 'Add rule'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

type AddMemberTab = 'person' | 'group'

function AddMemberModal({
  open,
  groupUid,
  existingDirectMembers,
  existingGrantGroups,
  onClose,
}: {
  open: boolean
  groupUid: string
  /** Person uids already in the effective-members list (excluded from the picker). */
  existingDirectMembers: Set<string>
  /** Directory-group uids already granted to this access group (excluded from the picker). */
  existingGrantGroups: Set<string>
  onClose: () => void
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()

  const [tab, setTab] = useState<AddMemberTab>('person')
  const [personSearch, setPersonSearch] = useState('')
  const [personQ, setPersonQ] = useState('')
  const [selectedPerson, setSelectedPerson] = useState('')
  const [groupSearch, setGroupSearch] = useState('')
  const [groupQ, setGroupQ] = useState('')
  const [selectedGroup, setSelectedGroup] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setPersonQ(personSearch.trim()), 300)
    return () => clearTimeout(t)
  }, [personSearch])
  useEffect(() => {
    const t = setTimeout(() => setGroupQ(groupSearch.trim()), 300)
    return () => clearTimeout(t)
  }, [groupSearch])

  const persons = usePersons(personQ)
  const groups = useGroups(groupQ)

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [scope, 'access-groups', groupUid, 'members'] })
    queryClient.invalidateQueries({ queryKey: [scope, 'access-groups', groupUid, 'directory-grants'] })
    queryClient.invalidateQueries({ queryKey: [scope, 'access-groups'] })
    queryClient.invalidateQueries({ queryKey: [scope, 'directory-groups'] })
  }

  const personOptions = useMemo(
    () =>
      (persons.data ?? [])
        .filter((p) => !existingDirectMembers.has(p.unique_id))
        .map((p) => ({ value: p.unique_id, label: `${p.display_name} · ${p.unique_id}` })),
    [persons.data, existingDirectMembers],
  )
  const groupOptions = useMemo(
    () =>
      (groups.data ?? [])
        .filter((g) => !existingGrantGroups.has(g.unique_id))
        .map((g) => ({ value: g.unique_id, label: `${g.name} · ${g.unique_id}` })),
    [groups.data, existingGrantGroups],
  )

  const createPerson = useMutation({
    mutationFn: () => api.createAccessGroupMember(groupUid, selectedPerson),
    onSuccess: (created) => {
      const added = (persons.data ?? []).find((p) => p.unique_id === selectedPerson)
      if (added && added.status === 'active') {
        // The members GET never exposes the oagm-… row uid (DELETE needs it), so
        // patch the cached row with the uid from this POST instead of refetching.
        queryClient.setQueryData<AccessGroupMember[]>([scope, 'access-groups', groupUid, 'members'], (old) => {
          const row: AccessGroupMember = {
            unique_id: added.unique_id,
            display_name: added.display_name,
            status: added.status,
            source: 'direct',
            member_uid: created.unique_id,
          }
          return [...(old ?? []).filter((m) => m.unique_id !== row.unique_id), row]
        })
      } else {
        invalidate()
      }
      onClose()
    },
  })

  const createGrant = useMutation({
    mutationFn: () => api.createDirectoryGrant(groupUid, selectedGroup),
    onSuccess: () => {
      invalidate()
      onClose()
    },
  })

  useEffect(() => {
    if (open) {
      setTab('person')
      setPersonSearch('')
      setPersonQ('')
      setSelectedPerson('')
      setGroupSearch('')
      setGroupQ('')
      setSelectedGroup('')
      createPerson.reset()
      createGrant.reset()
    }
    // reset fns are stable; only reset when the modal (re)opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const isPending = tab === 'person' ? createPerson.isPending : createGrant.isPending
  const createError = tab === 'person' ? createPerson.error : createGrant.error
  const canSubmit = tab === 'person' ? !!selectedPerson : !!selectedGroup

  const TabButton = ({ value, label }: { value: AddMemberTab; label: string }) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === value}
      onClick={() => setTab(value)}
      className={`flex-1 rounded-sm px-3 py-2 text-[10px] font-semibold uppercase tracking-widest transition-colors focus:outline-none focus:ring-2 focus:ring-ostrel-gold ${
        tab === value
          ? 'bg-ostrel-navy text-ostrel-card'
          : 'bg-ostrel-sand text-ostrel-ink-muted hover:bg-ostrel-sand/80 hover:text-ostrel-ink'
      }`}
    >
      {label}
    </button>
  )

  return (
    <Modal open={open} title="Add member" onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (tab === 'person') createPerson.mutate()
          else createGrant.mutate()
        }}
      >
        <div className="flex gap-2" role="tablist" aria-label="Member kind">
          <TabButton value="person" label="Person" />
          <TabButton value="group" label="Directory group" />
        </div>

        {tab === 'person' ? (
          <>
            <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
              Add a person directly to this access group. Direct members override rule- or
              group-derived memberships for that person.
            </p>
            <SearchableSelect
              label="Person"
              value={selectedPerson}
              onChange={(v) => {
                setSelectedPerson(v)
                const picked = personOptions.find((o) => o.value === v)
                if (picked) setPersonSearch(picked.label.split(' · ')[0])
              }}
              options={personOptions}
              placeholder="— search people —"
              searchPlaceholder="Start typing to search…"
              loading={persons.isPending}
              emptyText={persons.isPending ? 'Searching…' : 'No matches — this person may already be a member.'}
            />
          </>
        ) : (
          <>
            <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
              Grant membership to every active person in the chosen directory group — or any group
              below it.
            </p>
            <SearchableSelect
              label="Directory group"
              value={selectedGroup}
              onChange={(v) => {
                setSelectedGroup(v)
                const picked = groupOptions.find((o) => o.value === v)
                if (picked) setGroupSearch(picked.label.split(' · ')[0])
              }}
              options={groupOptions}
              placeholder="— search directory groups —"
              searchPlaceholder="Start typing to search…"
              loading={groups.isPending}
              emptyText={
                groups.isPending ? 'Searching…' : 'No matches — this group may already be granted.'
              }
            />
          </>
        )}

        {createError && <p className="text-xs text-ostrel-danger">{errText(createError)}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="navy" type="submit" disabled={isPending || !canSubmit}>
            {isPending ? 'Adding…' : tab === 'person' ? 'Add person' : 'Grant to group'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default function AccessGroupDetailPage() {
  const { uid = '' } = useParams()
  const navigate = useNavigate()
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()

  const groups = useAccessGroups()
  const members = useAccessGroupMembers(uid)
  const directoryGrants = useDirectoryGrants(uid)
  const effectiveDoors = useAccessGroupEffectiveDoors(uid)
  const policies = useDoorPolicies()
  const memberRules = useMemberRules()
  const schedules = useSchedules()
  const tree = useLocationTree()
  const doors = useDoors()
  const allGroups = useGroups()

  const group = groups.data?.find((g) => g.unique_id === uid)

  // Dry-run gating: a group adopted from a dry-run system can never push
  // member changes to the portal — member edits are read-only here.
  const dryRunHint = group ? readOnlyReason(group) : undefined
  const membersReadOnly = dryRunHint !== undefined
  const groupReadOnly = dryRunHint !== undefined

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('0')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [mergeOpen, setMergeOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [editPolicy, setEditPolicy] = useState<DoorPolicy | null>(null)
  const [confirmPolicy, setConfirmPolicy] = useState<DoorPolicy | null>(null)
  const [addRuleOpen, setAddRuleOpen] = useState(false)
  const [confirmRule, setConfirmRule] = useState<MemberRule | null>(null)
  const [addMemberOpen, setAddMemberOpen] = useState(false)
  const [confirmMember, setConfirmMember] = useState<AccessGroupMember | null>(null)
  const [confirmGrant, setConfirmGrant] = useState<DirectoryGrant | null>(null)

  useEffect(() => {
    if (group) {
      setName(group.name)
      setDescription(group.description)
      setPriority(String(group.priority))
    }
  }, [group])

  // [scope, 'access-groups'] prefix-matches the members query too, so policy
  // and rule edits also refresh the effective-members list.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [scope, 'access-groups'] })
    queryClient.invalidateQueries({ queryKey: [scope, 'door-policies'] })
    queryClient.invalidateQueries({ queryKey: [scope, 'member-rules'] })
  }
  const save = useMutation({
    mutationFn: () =>
      api.updateAccessGroup(uid, {
        name: name.trim(),
        description: description.trim(),
        priority: Number.parseInt(priority, 10) || 0,
      }),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: () => api.deleteAccessGroup(uid),
    onSuccess: () => {
      setConfirmDelete(false)
      invalidate()
      navigate('/access-groups')
    },
  })
  const mergeAccessGroup = useMergeAccessGroup()
  const accessGroupOptions = (groups.data ?? [])
    .filter((g) => g.unique_id !== uid)
    .map((g) => ({ value: g.unique_id, label: `${g.name} · ${g.unique_id}` }))
  const removePolicy = useMutation({
    mutationFn: api.deleteDoorPolicy,
    onSuccess: () => {
      invalidate()
      setConfirmPolicy(null)
    },
  })
  const removeRule = useMutation({
    mutationFn: api.deleteMemberRule,
    onSuccess: () => {
      invalidate()
      setConfirmRule(null)
    },
  })
  const removeMember = useMutation({
    mutationFn: (memberUid: string) => api.deleteAccessGroupMember(uid, memberUid),
    onSuccess: () => {
      invalidate()
      setConfirmMember(null)
    },
  })
  const removeGrant = useMutation({
    mutationFn: (grantUid: string) => api.deleteDirectoryGrant(uid, grantUid),
    onSuccess: () => {
      invalidate()
      setConfirmGrant(null)
    },
  })

  const schedulesByUid = useMemo(
    () => new Map((schedules.data ?? []).map((s) => [s.unique_id, s])),
    [schedules.data],
  )
  const scheduleName = (puid: string | null) =>
    puid ? (schedulesByUid.get(puid)?.name ?? puid) : null
  const groupNames = useMemo(
    () => new Map((allGroups.data ?? []).map((g) => [g.unique_id, g.name])),
    [allGroups.data],
  )
  // Active members derived via each directory-group grant — the grant row
  // shows this count instead of the members list expanding into every
  // person it covers (they're members of the directory group, not of this
  // access group directly; drill into the group to see them).
  const activeGrantMemberCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const m of members.data ?? []) {
      if (m.source === 'group' && m.grant_uid && m.status === 'active') {
        counts.set(m.grant_uid, (counts.get(m.grant_uid) ?? 0) + 1)
      }
    }
    return counts
  }, [members.data])
  const paths = useMemo(() => (tree.data ? locationPaths(tree.data) : new Map<string, string>()), [tree.data])
  const locationName = (luid: string) => paths.get(luid) ?? luid
  const doorsByUid = useMemo(
    () => new Map((doors.data ?? []).map((d) => [d.unique_id, d])),
    [doors.data],
  )
  const doorName = (duid: string) => {
    const d = doorsByUid.get(duid)
    return d ? doorLabel(d) : duid
  }

  if (groups.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10" />
        <Skeleton className="h-48" />
        <Skeleton className="h-40" />
      </div>
    )
  }
  if (groups.isError) {
    return <EmptyState title="Could not load access groups" hint={errText(groups.error)} />
  }
  if (!group) {
    return (
      <EmptyState
        title="Access group not found"
        hint={uid}
        action={
          <Button variant="ghost" size="sm" onClick={() => navigate('/access-groups')}>
            ← All access groups
          </Button>
        }
      />
    )
  }

  const groupPolicies = (policies.data ?? []).filter((p) => p.group === uid)
  const takenDoors = new Set(groupPolicies.map((p) => p.door))
  const groupRules = (memberRules.data ?? []).filter((r) => r.group === uid)
  const takenRuleLocations = new Set(groupRules.map((r) => r.location))

  return (
    <div className="flex flex-col gap-6">
      <PageHeader section="Access" title={group.name}>
        {group.description && <p className="mt-1 text-xs text-ostrel-ink-muted">{group.description}</p>}
        <p className="mt-1.5">
          <Chip tone="muted" label={`priority ${group.priority}`} />
        </p>
      </PageHeader>

      <Card>
        <div className="flex flex-col gap-4">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Edit group · <span className="tnum">{group.unique_id}</span>
          </p>
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
          />
          <Input
            label="Priority"
            type="number"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          />
          <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
            Per door, the highest-priority group with a decision wins — including an exclude. Ties
            favor include.
          </p>
          {save.isError && <p className="text-xs text-ostrel-danger">{errText(save.error)}</p>}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="navy"
              disabled={save.isPending || !name.trim() || groupReadOnly}
              title={dryRunHint}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Saving…' : 'Save changes'}
            </Button>
            <Button
              variant="ghost"
              disabled={groupReadOnly}
              title={dryRunHint}
              onClick={() => setMergeOpen(true)}
            >
              Merge…
            </Button>
            <Button variant="danger" disabled={groupReadOnly} title={dryRunHint} onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          </div>
        </div>
      </Card>

      <Card
        title="Members"
        action={
          <Button
            variant="ghost"
            size="sm"
            disabled={membersReadOnly}
            title={membersReadOnly ? dryRunHint : undefined}
            onClick={() => setAddMemberOpen(true)}
          >
            + Add member
          </Button>
        }
      >
        {membersReadOnly && <p className="mb-2 text-[11px] text-ostrel-ink-muted">{dryRunHint}</p>}
        {members.isPending || directoryGrants.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-7" />
            <Skeleton className="h-7" />
            <Skeleton className="h-7" />
          </div>
        ) : members.isError ? (
          <EmptyState title="Could not load members" hint={errText(members.error)} />
        ) : directoryGrants.isError ? (
          <EmptyState title="Could not load directory group grants" hint={errText(directoryGrants.error)} />
        ) : members.data.length === 0 && directoryGrants.data.length === 0 ? (
          <EmptyState
            title="No members"
            hint="Add a person directly, grant a whole directory group, or create a location rule to derive members from occupancies."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-ostrel-line">
            {members.data
              .filter((m) => m.source !== 'group')
              .map((m) => (
                <li
                  key={m.unique_id}
                  className="flex items-center gap-4 py-2.5"
                  title={
                    m.source === 'rule'
                      ? 'Derived from location rules — remove the rule or the occupancy to revoke.'
                      : undefined
                  }
                >
                  <span className="min-w-0 flex-1">
                    <Link
                      to={`/directory/${m.unique_id}`}
                      className="block truncate text-sm text-ostrel-ink hover:text-ostrel-accent"
                    >
                      {m.display_name}
                    </Link>
                    <span className="tnum block truncate text-[11px] text-ostrel-ink-muted">
                      {m.unique_id}
                    </span>
                  </span>
                  <Chip tone={sourceTone(m.source)} label={m.source} />
                  <Chip tone={m.status === 'active' ? 'ok' : 'muted'} label={m.status} />
                  {m.source === 'direct' &&
                    (m.member_uid ? (
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={membersReadOnly}
                        title={membersReadOnly ? dryRunHint : undefined}
                        onClick={() => setConfirmMember(m)}
                      >
                        Remove
                      </Button>
                    ) : (
                      <Button
                        variant="danger"
                        size="sm"
                        disabled
                        title={
                          membersReadOnly
                            ? dryRunHint
                            : 'This direct member was added elsewhere — its member uid is not exposed by the members list, so it cannot be removed here.'
                        }
                      >
                        Remove
                      </Button>
                    ))}
                </li>
              ))}
            {directoryGrants.data.map((g) => {
              const activeCount = activeGrantMemberCounts.get(g.unique_id) ?? 0
              return (
                <li key={g.unique_id} className="flex items-center gap-4 py-2.5">
                  <span className="min-w-0 flex-1">
                    <Link
                      to={`/groups/${g.group}`}
                      className="block truncate text-sm text-ostrel-ink hover:text-ostrel-accent"
                    >
                      {groupNames.get(g.group) ?? g.group}
                    </Link>
                    <span className="tnum block truncate text-[11px] text-ostrel-ink-muted">
                      {g.group}
                    </span>
                  </span>
                  <span className="tnum shrink-0 text-xs text-ostrel-ink-muted">
                    {activeCount} active member{activeCount === 1 ? '' : 's'}
                  </span>
                  <Chip tone="info" label="group" />
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={membersReadOnly}
                    title={membersReadOnly ? dryRunHint : undefined}
                    onClick={() => setConfirmGrant(g)}
                  >
                    Remove
                  </Button>
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      <Card
        title="Location rules"
        action={
          <Button
            variant="ghost"
            size="sm"
            disabled={groupReadOnly}
            title={dryRunHint}
            onClick={() => setAddRuleOpen(true)}
          >
            + Add rule
          </Button>
        }
      >
        {memberRules.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : memberRules.isError ? (
          <EmptyState title="Could not load location rules" hint={errText(memberRules.error)} />
        ) : groupRules.length === 0 ? (
          <EmptyState
            title="No location rules"
            hint="Add a rule to make occupants of a location members of this group."
          />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Location</TH>
                <TH>Rule</TH>
                <TH className="text-right">Action</TH>
              </tr>
            </THead>
            <TBody>
              {groupRules.map((r) => (
                <TR key={r.unique_id}>
                  <TD>
                    <span className="text-sm text-ostrel-ink">
                      <LocationLink uid={r.location}>{locationName(r.location)}</LocationLink>
                    </span>
                  </TD>
                  <TD>
                    <span className="tnum font-mono text-xs text-ostrel-ink-muted">{r.unique_id}</span>
                  </TD>
                  <TD className="text-right">
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={groupReadOnly}
                      title={dryRunHint}
                      onClick={() => setConfirmRule(r)}
                    >
                      Remove
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <Card
        title="Door policies"
        action={
          <Button
            variant="ghost"
            size="sm"
            disabled={groupReadOnly}
            title={dryRunHint}
            onClick={() => setAddOpen(true)}
          >
            + Add policy
          </Button>
        }
      >
        {groupPolicies.length === 0 ? (
          <EmptyState
            title="No door policies"
            hint="Add a policy to grant (or deny) this group access to a door."
          />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Door</TH>
                <TH>Mode</TH>
                <TH>Schedule</TH>
                <TH className="text-right">Action</TH>
              </tr>
            </THead>
            <TBody>
              {groupPolicies.map((p) => (
                <TR key={p.unique_id}>
                  <TD>
                    <span className="text-sm text-ostrel-ink">{doorName(p.door)}</span>
                  </TD>
                  <TD>
                    <Chip tone={p.mode === 'exclude' ? 'danger' : 'ok'} label={p.mode} />
                  </TD>
                  <TD>
                    {/* A vendor-derived policy can reference a non-door schedule the
                        pickers exclude — show it muted with its kind chip. */}
                    {(() => {
                      const sched = p.schedule ? schedulesByUid.get(p.schedule) : undefined
                      if (!p.schedule) return <span className="tnum text-xs text-ostrel-ink-muted">Always</span>
                      const nonDoor = sched !== undefined && sched.kind !== 'door'
                      return (
                        <span className="flex items-center gap-1.5">
                          <span className="tnum text-xs text-ostrel-ink-muted">{scheduleName(p.schedule)}</span>
                          {nonDoor && <Chip tone="muted" label={sched.kind} />}
                        </span>
                      )
                    })()}
                  </TD>
                  <TD className="text-right">
                    <span className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={groupReadOnly}
                        title={dryRunHint}
                        onClick={() => setEditPolicy(p)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={groupReadOnly}
                        title={dryRunHint}
                        onClick={() => setConfirmPolicy(p)}
                      >
                        Remove
                      </Button>
                    </span>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <Card title="Effective doors">
        {effectiveDoors.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : effectiveDoors.isError ? (
          <EmptyState title="Could not load effective doors" hint={errText(effectiveDoors.error)} />
        ) : effectiveDoors.data.length === 0 ? (
          <EmptyState
            title="No effective doors"
            hint="This group's door policies do not resolve to any door."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-ostrel-line">
            {effectiveDoors.data.map((d) => (
              <li key={d.door} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                <span className="min-w-0 flex-1">
                  <span
                    className={`block truncate text-sm font-medium ${
                      d.mode === 'exclude' ? 'text-ostrel-ink-muted line-through' : 'text-ostrel-ink'
                    }`}
                  >
                    {d.name}
                  </span>
                  <span className="block truncate text-[11px] text-ostrel-ink-muted">{d.path.join(' › ')}</span>
                </span>
                {d.mode === 'exclude' ? (
                  <Chip tone="danger" label="exclude" />
                ) : (
                  <span className="tnum shrink-0 text-xs text-ostrel-ink-muted">
                    {scheduleName(d.schedule) ?? 'Always'}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <MergeModal
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        title="Merge access group"
        description="Pick another access group to merge into. The current group is deleted and its door policies, location rules, members and directory grants move to the target."
        source={{
          unique_id: group.unique_id,
          label: group.name,
          kind: 'access group',
        }}
        options={accessGroupOptions}
        loading={groups.isPending}
        pending={mergeAccessGroup.isPending}
        error={mergeAccessGroup.error}
        onConfirm={(target) =>
          mergeAccessGroup.mutate(
            { uid, target },
            {
              onSuccess: () => {
                setMergeOpen(false)
                navigate(`/access-groups/${target}`)
              },
            },
          )
        }
      />

      <ConfirmDeleteModal
        open={confirmDelete}
        title="Delete access group"
        objectName={group.name}
        expected={group.unique_id}
        pending={remove.isPending}
        error={remove.isError ? errText(remove.error) : null}
        confirmLabel={remove.isPending ? 'Deleting…' : 'Delete'}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirmDelete(false)}
      >
        <p>
          Delete <span className="font-medium">{group.name}</span>? Its door policies and member
          rules are removed with it.
        </p>
      </ConfirmDeleteModal>

      <ConfirmDeleteModal
        open={confirmPolicy !== null}
        title="Remove door policy"
        objectName={confirmPolicy ? doorName(confirmPolicy.door) : ''}
        expected={confirmPolicy?.unique_id ?? ''}
        pending={removePolicy.isPending}
        error={removePolicy.isError ? errText(removePolicy.error) : null}
        confirmLabel={removePolicy.isPending ? 'Removing…' : 'Remove'}
        onConfirm={() => confirmPolicy && removePolicy.mutate(confirmPolicy.unique_id)}
        onClose={() => setConfirmPolicy(null)}
      >
        <p>
          Remove the <span className="font-medium">{confirmPolicy?.mode}</span> policy on{' '}
          <span className="font-medium">
            {confirmPolicy ? doorName(confirmPolicy.door) : ''}
          </span>
          ? Members lose this group's decision for the door.
        </p>
      </ConfirmDeleteModal>

      <ConfirmDeleteModal
        open={confirmRule !== null}
        title="Remove location rule"
        objectName={confirmRule ? locationName(confirmRule.location) : ''}
        expected={confirmRule?.unique_id ?? ''}
        pending={removeRule.isPending}
        error={removeRule.isError ? errText(removeRule.error) : null}
        confirmLabel={removeRule.isPending ? 'Removing…' : 'Remove'}
        onConfirm={() => confirmRule && removeRule.mutate(confirmRule.unique_id)}
        onClose={() => setConfirmRule(null)}
      >
        <p>
          Remove the location rule at{' '}
          <span className="font-medium">{confirmRule ? locationName(confirmRule.location) : ''}</span>
          ? Occupants there stop being members of this group.
        </p>
      </ConfirmDeleteModal>

      <ConfirmDeleteModal
        open={confirmMember !== null}
        title="Remove direct member"
        objectName={confirmMember?.display_name ?? ''}
        expected={confirmMember?.member_uid ?? ''}
        pending={removeMember.isPending}
        error={removeMember.isError ? errText(removeMember.error) : null}
        confirmLabel={removeMember.isPending ? 'Removing…' : 'Remove'}
        onConfirm={() => confirmMember?.member_uid && removeMember.mutate(confirmMember.member_uid)}
        onClose={() => setConfirmMember(null)}
      >
        <p>
          Remove <span className="font-medium">{confirmMember?.display_name}</span> as a direct
          member of this group? If it was their last direct assignment, their rule- or
          group-derived memberships take effect again.
        </p>
      </ConfirmDeleteModal>

      <ConfirmDeleteModal
        open={confirmGrant !== null}
        title="Remove directory group grant"
        objectName={groupNames.get(confirmGrant?.group ?? '') ?? confirmGrant?.group ?? ''}
        expected={confirmGrant?.unique_id ?? ''}
        pending={removeGrant.isPending}
        error={removeGrant.isError ? errText(removeGrant.error) : null}
        confirmLabel={removeGrant.isPending ? 'Removing…' : 'Remove'}
        onConfirm={() => confirmGrant && removeGrant.mutate(confirmGrant.unique_id)}
        onClose={() => setConfirmGrant(null)}
      >
        <p>
          Remove the directory group grant? People will stop being members via this grant, but
          direct assignments and location rules remain.
        </p>
      </ConfirmDeleteModal>

      <AddPolicyModal
        open={addOpen}
        groupUid={uid}
        takenDoors={takenDoors}
        onClose={() => setAddOpen(false)}
      />
      <EditPolicyModal policy={editPolicy} onClose={() => setEditPolicy(null)} />
      <AddRuleModal
        open={addRuleOpen}
        groupUid={uid}
        takenLocations={takenRuleLocations}
        onClose={() => setAddRuleOpen(false)}
      />
      <AddMemberModal
        open={addMemberOpen}
        groupUid={uid}
        existingDirectMembers={new Set((members.data ?? []).filter((m) => m.source === 'direct').map((m) => m.unique_id))}
        existingGrantGroups={new Set((directoryGrants.data ?? []).map((g) => g.group))}
        onClose={() => setAddMemberOpen(false)}
      />
    </div>
  )
}
